import { Drawer } from '@mantine/core';
import type { Id, PaymentPlanComplete } from 'api/models';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { useState } from 'react';
import { isNonNullish } from 'remeda';
import Amount from 'shared/components/Amount';
import { Icon, IconName } from 'shared/components/Icon';
import type { Debtor } from 'types/store/client-state';

import PaymentPlanDetailDebtor from '../PaymentPlanDetailDebtor';

import styleIdentifiers from './DebtorReactionPaymentPlansList.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  items: PaymentPlanComplete[];
  data: any;
};

export default function DebtorReactionPaymentPlansList({ items, data }: Props) {
  const { t, currentLang } = useTranslation();

  const [detailedPaymentPlan, setDetailedPaymentPlan] = useState<PaymentPlanComplete | null>(null);

  const showDetails = (id: Id) => {
    const paymentPlan = items.find(item => item.id === id);
    if (isNonNullish(paymentPlan)) setDetailedPaymentPlan(paymentPlan);
  };

  const hideDetails = () => setDetailedPaymentPlan(null);

  const getStatus = (completed: any) => (completed ? i18nKeys.COMPLETED : i18nKeys.CANCELLED);

  return (
    <div className={styles('debtor-reaction-payment-plans-list')}>
      <div className={styles('payment-plan-list')}>
        {items?.length > 0 ? (
          items.map(
            ({
              id,
              attributes: {
                remaining_balance,
                currency,
                created_at,
                next_due_date,
                cancelled,
                completed,
              },
            }) => (
              <div
                key={id}
                className={styles(
                  'payment-plan-item',
                  cancelled && 'cancelled',
                  completed && 'completed',
                )}
              >
                <div className={styles('payment-plan-info')} onClick={() => showDetails(id)}>
                  <div>
                    <div className={styles('reference')}>
                      {`${t(i18nKeys.PAYMENT_PLAN.TITLE.ONE)} #${id}`}
                    </div>
                    <div className={styles('amount')}>
                      <Amount
                        localizedValue={remaining_balance}
                        value={remaining_balance}
                        suffix={currency}
                        debtor={data.debtor_data as Debtor}
                      />
                    </div>
                  </div>
                  <div className={styles('date')}>
                    <div>
                      <span className={styles('payment-plan-subtitle')}>
                        {t(i18nKeys.PAYMENT_PLAN.ATTRIBUTES.CREATED_AT)}{' '}
                      </span>
                      {dayjs(created_at).format('L')}
                    </div>
                    <div>
                      {cancelled || completed ? (
                        t(getStatus(completed))
                      ) : (
                        <>
                          <span className={styles('payment-plan-subtitle')}>
                            {t(i18nKeys.PAYMENT_PLAN.ATTRIBUTES.NEXT_DUE_DATE)}{' '}
                          </span>
                          {dayjs(next_due_date).format('L')}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles('actions-container')} onClick={() => showDetails(id)}>
                  <Icon name={IconName.EYE} />
                </div>
              </div>
            ),
          )
        ) : (
          <div className={styles('no-payment-plan')}>{t(i18nKeys.NO_RESULT)}</div>
        )}
      </div>
      <Drawer position="right" opened={isNonNullish(detailedPaymentPlan)} onClose={hideDetails}>
        {isNonNullish(detailedPaymentPlan) && (
          <PaymentPlanDetailDebtor paymentPlan={detailedPaymentPlan} />
        )}
      </Drawer>
    </div>
  );
}
